import type { Key } from 'react';
import { useMemo, useState } from 'react';

import capitalize from 'lodash/capitalize';
import qs from 'qs';

import {
	Button,
	Chip,
	PetitionCard,
	SelectMenu,
	SelectMenuContent,
	SelectMenuItem,
	SelectMenuTriggerButton,
	Text,
} from '@change/ds-core-ui';
import { ExpandIcon, FilterIcon } from '@change/ds-icons';

import { Translate, TranslatePlural, useI18n } from '@change-corgi/core/react/i18n';
import { VisuallyHidden } from '@change-corgi/design-system-legacy/a11y';
import { Loader } from '@change-corgi/design-system-legacy/components/progressiveDisclosure';

import { PromotedImpressionTracker } from 'src/app/shared/components/promotionTracking';
import { useLazyLoadedListFocus } from 'src/app/shared/hooks/focus';

import { TagsFilter } from './components/TagsFilter';
import { useFilterablePetitionsSection } from './hooks/useFilterablePetitionsSection';

// eslint-disable-next-line complexity, max-lines-per-function
export const FilterablePetitionsSection = () => {
	const { containerRef, loadMoreRef, loadingRef, getListItemRef, recordListEvent, lastEvent } =
		useLazyLoadedListFocus();
	const {
		data: {
			sections,
			totalCountLocalized,
			displayName,
			refreshLoading,
			actions: { trackTrending, trackMostRecent },
		},
	} = useFilterablePetitionsSection();

	const [filteredSectionId, setFilteredSectionId] = useState<Key>(sections[0].id);
	const currentSection = useMemo(
		() => sections.find((section) => section.id === filteredSectionId),
		[sections, filteredSectionId],
	);

	const { translate, translatePlural } = useI18n();

	const handleSelectionChange = (id: Key | null) => {
		if (id === null) return;

		if (id === 'most-recent') {
			trackMostRecent();
		} else {
			trackTrending();
		}

		setFilteredSectionId(id);
	};

	return (
		<div className="flex flex-col text-typography-strong" data-qa="explore-petitions-container">
			<div className="flex items-center justify-between pb-6 max-md:flex-col max-md:items-start max-md:gap-6">
				<Text as="h2" variant="heading" size="small" data-qa="explore-petitions-heading">
					<Translate
						value="corgi.local.petition_section.heading"
						replacements={{ location: displayName, countLocalized: totalCountLocalized }}
					/>
				</Text>

				<SelectMenu
					onSelectionChange={(id) => handleSelectionChange(id)}
					data-testid="topic-select-menu"
					ariaLabel="label"
					defaultSelectedId={sections[0]?.id}
				>
					<SelectMenuTriggerButton className="rounded-sm">
						{(value) => (
							<Chip
								forceInteractive
								leadingIcon={<FilterIcon size="small" />}
								trailingIcon={<ExpandIcon />}
								data-qa="topic-multi-select-button"
							>
								<Translate
									value="corgi.local.petition_section.sort_by"
									replacements={{ label: capitalize(value?.label) }}
								/>
							</Chip>
						)}
					</SelectMenuTriggerButton>

					<SelectMenuContent>
						<SelectMenuItem
							data-testid="topic-select-trending"
							id={sections[0]?.id}
							label={translate('corgi.local.petition_section.trending')}
						/>
						<SelectMenuItem
							data-testid="topic-select-most-recent"
							id={sections[1]?.id}
							label={translate('corgi.local.petition_section.most_recent')}
						/>
					</SelectMenuContent>
				</SelectMenu>
			</div>

			<TagsFilter filteredSectionId={filteredSectionId} />

			<div ref={containerRef} className="flex flex-col items-center gap-4">
				<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
					{refreshLoading ? (
						<Loader size={44} mx="auto" mt={24} data-testid="refresh-loading-loader" />
					) : (
						currentSection?.petitions.map(({ node: petition, promotionToken }, index) => {
							return (
								<div className="flex" key={petition.id}>
									{lastEvent.type === 'loadMore' && index === lastEvent.firstNewItemIndex && (
										<VisuallyHidden role="alert">
											<TranslatePlural
												value="corgi.topic_page.petition_section.fetched_more"
												count={currentSection.petitions.length - lastEvent.previousItemsCount}
												replacements={{ count: currentSection.petitions.length - lastEvent.previousItemsCount }}
											/>
										</VisuallyHidden>
									)}

									{promotionToken && (
										<PromotedImpressionTracker
											petitionId={petition.id}
											placement="local_page"
											promotionToken={promotionToken}
										/>
									)}
									<PetitionCard
										ref={getListItemRef(index)}
										data-qa="explore-petitions-petition-card"
										petitionId={petition.id}
										className="w-full"
										petitionLinkUrl={`/p/${encodeURIComponent(petition.slug)}${qs.stringify({ source_location: 'local_page', ...(promotionToken && { pt: promotionToken }) }, { addQueryPrefix: true })}`}
										signaturesLabel={translatePlural(
											'corgi.components.petition_card.signatures',
											petition.signatureState.signatureCount.displayed,
											{
												countLocalized: petition.signatureState.signatureCount.displayedLocalized,
											},
										)}
										imageUrl={petition.photo?.petitionMedium.url}
										title={petition.ask}
										key={petition.id}
										// TODO Where should this value come from
										location={`${displayName}, US`}
									/>
								</div>
							);
						})
					)}
				</div>

				{currentSection?.nextPageLoading && (
					<>
						<VisuallyHidden role="alert">
							<Translate value="corgi.tag.petitions.fetching_more" />
						</VisuallyHidden>
						{/* TODO: DS loading state */}
						<Loader size={44} mx="auto" ref={loadingRef} />
					</>
				)}

				{!currentSection?.nextPageLoading && currentSection?.hasNextPage && (
					<Button
						size="small"
						className="max-w-[348px] max-md:!w-full"
						data-qa="explore-petitions-load-more-button"
						data-testid="explore-petitions-load-more-button"
						onClick={() => {
							recordListEvent({ type: 'loadMore', itemsCount: currentSection.petitions.length });
							currentSection.loadMore();
						}}
						variant="secondary"
						ref={loadMoreRef}
					>
						<Translate value="corgi.local.petition_section.show_more" />
					</Button>
				)}
			</div>
		</div>
	);
};
